import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MenuItem, MenuItemDivider, ToastNotification } from '@carbon/react';
import { Add, Checkmark, Settings, Store, Warning } from '@carbon/react/icons';
import { ALL_BRANCHES } from 'src/context/types';
import { useTenantContext } from 'src/context/useTenantContext';
import { useAuthPermissions } from 'src/hooks/useAuthPermissions';
import { useMemberships } from 'src/hooks/useOrganizations';
import { useBranches } from 'src/hooks/useRestaurantBranches';
import { useRestaurants } from 'src/hooks/useRestaurants';
import { EntityAvatar } from './EntityAvatar';
import { HeaderSwitcher } from './HeaderSwitcher';
import { useContextSwitch } from './useContextSwitch';

/**
 * Carbon marks the current row by its icon; there is no selected state on a plain MenuItem.
 * MenuItem has a single icon slot, so the current row spends it on a checkmark and every
 * other row spends it on its type icon — no row renders a bare gutter.
 */
const rowIcon = (current: boolean, fallback: typeof Checkmark) => (current ? Checkmark : fallback);

/**
 * Organization and restaurant rows show the entity's own logo on EVERY row, current one
 * included — a logo row cannot also spend its single icon slot on a checkmark. Selection is
 * carried by role/aria-checked (menuitemradio semantics a screen reader actually announces)
 * plus a `.shell-menu-item--current` accent, not by swapping the icon. Passed props reach
 * MenuItem's <li> via its rest-spread, which runs after the default `role: "menuitem"`, so
 * `role`/`aria-checked` here do win; `className` merges through MenuItem's own `cx(...)`
 * rather than overwriting it, so `shell-menu-item--current` composes with the base classes.
 */
const entityRowProps = (current: boolean, name: string, logo?: string | null) => ({
  role: 'menuitemradio' as const,
  'aria-checked': current,
  className: current ? 'shell-menu-item--current' : undefined,
  renderIcon: () => <EntityAvatar name={name} logo={logo} shape="square" />,
});

export const ContextBreadcrumb = React.memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  const { canManageOrg, canViewAllBranches } = useAuthPermissions();
  const { selectOrganization, goRestaurant, goBranch, switchError, clearSwitchError } = useContextSwitch();

  const { data: memberships } = useMemberships();
  const { data: restaurants, isLoading: restaurantsLoading, isError: restaurantsFailed } = useRestaurants();
  const { data: branches, isError: branchesFailed } = useBranches();

  const organizations = memberships ?? [];
  const restaurantList = restaurants?.data ?? [];
  const branchList = branches?.data ?? [];

  const currentOrg = organizations.find((organization) => organization.slug === orgSlug);
  const currentRestaurant = restaurantList.find((restaurant) => restaurant.id === restaurantId);

  // On first paint, and again right after an org switch (which blanks restaurantId), the
  // restaurant query is mid-flight and there is no name to show yet. A skeleton avoids the
  // empty-icon-then-reflow flash that an empty text node produces.
  const restaurantNameUnknown = restaurantsLoading && !currentRestaurant;

  // A member cannot list branches (the endpoint is owner/admin-gated), so there is nothing
  // to resolve the id against and the label falls back rather than rendering a raw uuid.
  const branchName =
    branchId === ALL_BRANCHES ? t('common.all') : branchList.find((branch) => branch.id === branchId)?.name;

  const failedRow = (key: string) => (
    <MenuItem key={key} label={t('header.loadFailed')} renderIcon={Warning} disabled />
  );

  return (
    <nav className="shell-breadcrumb" aria-label={t('header.switchStoreBranch')}>
      <HeaderSwitcher
        id="org-switcher-button"
        label={t('header.switchOrganization')}
        text={currentOrg?.name ?? orgSlug}
        className="shell-switcher--org"
        renderMark={() => (
          <EntityAvatar name={currentOrg?.name ?? orgSlug} logo={currentOrg?.logo} size={20} shape="square" />
        )}
      >
        {organizations.map((organization) => (
          <MenuItem
            key={organization.id}
            label={organization.name}
            {...entityRowProps(organization.slug === orgSlug, organization.name, organization.logo)}
            onClick={() => void selectOrganization(organization.id, organization.slug)}
          />
        ))}
        <MenuItemDivider />
        <MenuItem label={t('header.createOrganization')} renderIcon={Add} onClick={() => navigate('/onboarding')} />
        {canManageOrg && (
          <MenuItem
            label={t('header.organizationSettings')}
            renderIcon={Settings}
            onClick={() => navigate('/settings/organization')}
          />
        )}
      </HeaderSwitcher>

      <span className="shell-breadcrumb__separator shell-breadcrumb__separator--org" aria-hidden>
        /
      </span>

      {restaurantNameUnknown ? (
        <span id="current-restaurant" className="shell-switcher shell-switcher--skeleton" aria-hidden />
      ) : (
        <HeaderSwitcher
          id="current-restaurant"
          label={t('header.restaurant')}
          text={currentRestaurant?.name ?? ''}
          renderMark={() => (
            <EntityAvatar
              name={currentRestaurant?.name ?? ''}
              logo={currentRestaurant?.logo}
              size={20}
              shape="square"
            />
          )}
          interactive={restaurantList.length > 1 || restaurantsFailed}
        >
          {restaurantList.length
            ? restaurantList.map((restaurant) => (
                <MenuItem
                  key={restaurant.id}
                  label={restaurant.name}
                  {...entityRowProps(restaurant.id === restaurantId, restaurant.name, restaurant.logo)}
                  onClick={() => void goRestaurant(restaurant.id)}
                />
              ))
            : restaurantsFailed
            ? failedRow('restaurants-failed')
            : null}
        </HeaderSwitcher>
      )}

      <span className="shell-breadcrumb__separator" aria-hidden>
        /
      </span>

      <HeaderSwitcher
        id="current-restaurant-branch"
        label={t('header.branch')}
        text={branchName ?? t('common.all')}
        renderMark={() => <Store size={20} />}
        interactive={branchList.length > 1 || canViewAllBranches || branchesFailed}
      >
        {canViewAllBranches && (
          <MenuItem
            label={t('common.all')}
            renderIcon={rowIcon(branchId === ALL_BRANCHES, Store)}
            onClick={() => goBranch(ALL_BRANCHES)}
          />
        )}
        {branchList.length
          ? branchList.map((branch) => (
              <MenuItem
                key={branch.id}
                label={branch.name}
                renderIcon={rowIcon(branch.id === branchId, Store)}
                onClick={() => goBranch(branch.id)}
              />
            ))
          : branchesFailed
          ? failedRow('branches-failed')
          : null}
      </HeaderSwitcher>

      {switchError && (
        <ToastNotification
          className="shell-switch-error"
          kind="error"
          lowContrast
          title={t('header.switchFailed')}
          subtitle={switchError}
          onClose={clearSwitchError}
        />
      )}
    </nav>
  );
});
